import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { useNavigate } from 'react-router-dom'
import { createSampleLeague } from '@/utils/createSampleLeague'
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/AppSidebar'
import { AppHeader } from '@/components/AppHeader'
import { NextMatchCard } from '@/components/NextMatchCard'
import { YourLeagues } from '@/components/YourLeagues'
import { RankingProgress } from '@/components/RankingProgress'
import { LatestNews } from '@/components/LatestNews'
import { NotificationsPanel } from '@/components/NotificationsPanel'
import { QuickActions } from '@/components/QuickActions'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Trophy, Users, Calendar, Target, Star, TrendingUp, Clock, Plus, Search } from 'lucide-react'

interface League {
  id: string
  name: string
  owner_id: string
  is_private: boolean
  password_hash?: string
  draft_at?: string
  starters_count: number
  bench_count: number
  max_teams: number
  created_at: string
  updated_at: string
}

interface UserProfile {
  id: string
  user_id: string
  username: string
  display_name?: string
  avatar_url?: string
  level: number
  xp: number
  aura_xp: number
  current_realm: string
  consecutive_submissions: number
  victory_points: number
  field_passes?: number
  created_at: string
  updated_at: string
}

interface Match {
  id: string
  league_id: string
  home_team: string
  away_team: string
  home_team_logo?: string
  away_team_logo?: string
  scheduled_time: string
  status: string
  home_score?: number
  away_score?: number
  created_at: string
  updated_at: string
}

interface LeagueParticipant {
  id: string
  league_id: string
  user_id: string
  rank?: number
  points: number
  joined_at: string
}

interface Notification {
  id: string
  user_id: string
  title: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  read: boolean
  action_url?: string
  created_at: string
}

const Dashboard = () => {
  const { user } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [userLeagues, setUserLeagues] = useState<League[]>([])
  const [nextMatch, setNextMatch] = useState<Match & { league_name?: string } | null>(null)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user) {
      fetchData()
    }
  }, [user])

  const fetchData = async () => {
    if (!user) return

    try {
      // Fetch user profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single()

      if (profileError) {
        console.error('Profile error:', profileError)
      } else {
        setUserProfile(profileData)
      }

      // Fetch user's leagues with participant counts
      const { data: leaguesData, error: leaguesError } = await supabase
        .from('league_players')
        .select(`
          *,
          leagues (
            *,
            league_players (count)
          )
        `)
        .eq('user_id', user.id)

      if (leaguesError) {
        console.error('Leagues error:', leaguesError)
      } else if (leaguesData) {
        const formattedLeagues = leaguesData.map(player => {
          const league = player.leagues as any
          return {
            ...league,
            player_id: player.id, // Store the player ID for navigation
            participants_count: league.league_players?.[0]?.count || 0
          }
        })
        setUserLeagues(formattedLeagues)
      }

      // Fetch next match for user's leagues
      if (leaguesData && leaguesData.length > 0) {
        const leagueIds = leaguesData.map(p => p.league_id)
        
        const { data: matchData, error: matchError } = await supabase
          .from('matches')
          .select(`
            *,
            leagues!inner(name)
          `)
          .in('league_id', leagueIds)
          .eq('status', 'scheduled')
          .gte('scheduled_time', new Date().toISOString())
          .order('scheduled_time', { ascending: true })
          .limit(1)
          .single()

        if (matchError) {
          console.log('No upcoming matches found')
        } else if (matchData) {
          setNextMatch({
            ...matchData,
            league_name: (matchData.leagues as any)?.name
          })
        }
      }

      // Fetch notifications
      const { data: notificationsData, error: notificationsError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10)

      if (notificationsError) {
        console.error('Notifications error:', notificationsError)
      } else {
        setNotifications(notificationsData as Notification[] || [])
      }

    } catch (error) {
      console.error('Error fetching data:', error)
      toast({
        title: 'Error loading data',
        description: 'Please try refreshing the page.',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleNotificationRead = async (id: string) => {
    try {
      await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', id)
      
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      )
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const handleMarkAllNotificationsRead = async () => {
    try {
      await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user?.id)
        .eq('read', false)
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  }

  const handleCreateSampleLeague = async () => {
    try {
      setLoading(true)
      await createSampleLeague()
      toast({
        title: 'Sample League Created!',
        description: 'Premier League Draft Test is ready for testing.',
      })
      // Refresh data to show new league
      await fetchData()
    } catch (error) {
      console.error('Error creating sample league:', error)
      toast({
        title: 'Error',
        description: 'Failed to create sample league. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your arena...</p>
        </div>
      </div>
    )
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        
        <SidebarInset className="flex-1">
          <AppHeader 
            userProfile={userProfile ? {
              username: userProfile.username,
              avatar_url: userProfile.avatar_url,
              victory_points: userProfile.victory_points,
              field_passes: userProfile.field_passes || 0
            } : undefined}
            notifications={notifications}
            onNotificationRead={handleNotificationRead}
          />
          
          <main className="flex-1 p-6">
            {/* Welcome Section */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold mb-2">
                Welcome {userProfile?.display_name || userProfile?.username || user?.email?.split('@')[0] || 'Champion'}!
              </h1>
            </div>

            {/* Main Grid Layout */}
            <div className="grid grid-cols-12 gap-6">
              {/* Left Column - 8/12 */}
              <div className="col-span-12 lg:col-span-8 space-y-6">
                {/* Next Match */}
                <NextMatchCard match={nextMatch || undefined} />
                
                {/* Your Leagues */}
                <YourLeagues 
                  leagues={userLeagues}
                  onCreateLeague={() => navigate('/leagues/create')}
                  onJoinLeague={() => navigate('/leagues')}
                  onViewLeague={(id) => navigate(`/league/${id}`)}
                />
                
                {/* Ranking Progress */}
                <RankingProgress 
                  level={userProfile?.level || 1}
                  auraXP={userProfile?.aura_xp || 0}
                  currentRealm={userProfile?.current_realm || 'Challenger'}
                  consecutiveSubmissions={userProfile?.consecutive_submissions || 0}
                  onViewRewards={() => navigate('/rewards')}
                />
              </div>

              {/* Right Column - 4/12 */}
              <div className="col-span-12 lg:col-span-4 space-y-6">
                {/* Latest News */}
                <LatestNews news={[]} />
                
                {/* Notifications */}
                <NotificationsPanel 
                  notifications={notifications}
                  onMarkAsRead={handleNotificationRead}
                  onMarkAllAsRead={handleMarkAllNotificationsRead}
                />
                
                {/* Quick Actions */}
                <QuickActions 
                  onCreateLeague={() => navigate('/leagues/create')}
                  onJoinLeague={() => navigate('/leagues')}
                />
                
                {/* League Browser Card */}
                <Card className="hover:shadow-lg transition-all duration-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-primary" />
                      League Browser
                    </CardTitle>
                    <CardDescription>
                      Discover and join leagues from around the community
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => navigate('/leagues')}
                    >
                      <Search className="h-4 w-4 mr-2" />
                      Browse All Leagues
                    </Button>
                    <Button 
                      variant="secondary" 
                      className="w-full"
                      onClick={handleCreateSampleLeague}
                      disabled={loading}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Test League
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  )
}

export default Dashboard